import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Icon } from './Icon';
import type { ProjectGalleryItem } from '../types/gallery';

interface GalleryModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: ProjectGalleryItem | null;
}

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled(motion.div)`
  background: ${props => props.theme.colors.white};
  border-radius: 1rem;
  max-width: 90vw;
  max-height: 90vh;
  width: 100%;
  max-width: 62.5rem;
  overflow-y: auto;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 2.5rem;
  height: 2.5rem;
  border: none;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.2rem;
  z-index: 10;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
    transform: scale(1.1);
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 25rem;
  overflow: hidden;
  border-radius: 1rem 1rem 0 0;

  @media (min-width: ${props => props.theme.breakpoints.tablet}) {
    height: 31.25rem;
  }
`;

const MainImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ImageNav = styled.div`
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.5rem;
  background: rgba(0, 0, 0, 0.5);
  padding: 0.5rem;
  border-radius: 2rem;
`;

const ImageDot = styled.button<{ isActive: boolean }>`
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 50%;
  border: none;
  background: ${props => props.isActive ? 'white' : 'rgba(255, 255, 255, 0.5)'};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: white;
    transform: scale(1.2);
  }
`;

const ContentSection = styled.div`
  padding: 2rem;
`;

const CategoryBadge = styled.span`
  display: inline-block;
  background: ${props => props.theme.gradients.primary};
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const Title = styled.h2`
  font-family: ${props => props.theme.fonts.heading};
  font-size: 1.75rem;
  color: ${props => props.theme.colors.text};
  margin-bottom: 1rem;

  @media (min-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 2rem;
  }
`;

const Description = styled.p`
  color: ${props => props.theme.colors.textLight};
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const DetailsSection = styled.div`
  margin-top: 1.5rem;
`;

const DetailsTitle = styled.h3`
  font-family: ${props => props.theme.fonts.heading};
  font-size: 1.25rem;
  color: ${props => props.theme.colors.text};
  margin-bottom: 1rem;
`;

const DetailsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const DetailItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  color: ${props => props.theme.colors.textLight};
  
  svg {
    flex: 0 0 auto;
    color: ${props => props.theme.colors.primary};
  }
`;

const ContactButton = styled(motion.button)`
  background: ${props => props.theme.gradients.primary};
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 1.5rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: ${props => props.theme.shadows.md};

  &:hover {
    transform: translateY(-0.125rem);
    box-shadow: ${props => props.theme.shadows.lg};
  }
`;

export const GalleryModal: React.FC<GalleryModalProps> = ({ isOpen, onClose, item }) => {
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleWhatsAppInquiry = () => {
    if (!item) return;
    
    const message = `Hola, vi el proyecto "${item.title}" en su galería y me interesa obtener más información y un presupuesto similar. ¿Podrían ayudarme?`;
    window.open(`https://wa.me/529513214264?text=${encodeURIComponent(message)}`, '_blank');
  };

  if (!item) return null;

  const images = item.images ?? [item.imageUrl];
  const currentImage = images[currentImageIndex];

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <ModalContent
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <CloseButton onClick={onClose} aria-label="Cerrar galería"><Icon name="X" size={20} /></CloseButton>
            
            <ImageContainer>
              <MainImage src={currentImage} alt={item.imageAlt} />
              
              {images.length > 1 && (
                <ImageNav>
                  {images.map((_, index) => (
                    <ImageDot
                      key={index}
                      isActive={index === currentImageIndex}
                      onClick={() => setCurrentImageIndex(index)}
                    />
                  ))}
                </ImageNav>
              )}
            </ImageContainer>

            <ContentSection>
              <CategoryBadge>{item.category}</CategoryBadge>
              <Title>{item.title}</Title>
              
              {item.description && (
                <Description>{item.description}</Description>
              )}

              {item.details && item.details.length > 0 && (
                <DetailsSection>
                  <DetailsTitle>Características del Proyecto</DetailsTitle>
                  <DetailsList>
                    {item.details.map((detail, index) => (
                      <DetailItem key={index}><Icon name="BadgeCheck" size={16} />{detail}</DetailItem>
                    ))}
                  </DetailsList>
                </DetailsSection>
              )}

              <ContactButton
                onClick={handleWhatsAppInquiry}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Icon name="MessageCircle" size={18} />
                Solicitar Presupuesto Similar
              </ContactButton>
            </ContentSection>
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
};
